import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import { Brain, Search, X, Check, Info, Layers, Download, Swords, Lock } from 'lucide-react';
import type { SubnetMindshare, SubnetMindshareHistory } from '../../types/mindshare';
import { getTensorpulseMindshareHistory } from '../../services/tensorpulseMindshareService';
import { subnetNames } from '../../utils/subnetNames';
import { downloadChartAsPng } from '../../utils/chartHelpers';
import { subnetDescriptions } from '../../utils/subnetDescriptions';
import { usePremiumStatus } from '../../hooks/usePremiumStatus';

interface CompareViewProps {
  mindshareData: SubnetMindshare[];
  loading: boolean;
  subnetHistories?: Record<number, SubnetMindshareHistory>;
}

type MetricType = 'mindsharePercentage' | 'twitterEngagement' | 'marketCapScore' | 'rankScore' | 'volumeScore';

interface SelectedSubnet {
  netuid: number;
  name: string;
  color: string;
  history: SubnetMindshareHistory | null;
  loading: boolean;
}

const COLORS = [
  'rgba(255, 255, 255, 1)',   // white
  'rgba(59, 130, 246, 1)',   // blue
  'rgba(16, 185, 129, 1)',   // green
  'rgba(139, 92, 246, 1)',   // purple
  'rgba(249, 115, 22, 1)',   // orange
  'rgba(236, 72, 153, 1)',   // pink
  'rgba(234, 179, 8, 1)',    // yellow
  'rgba(20, 184, 166, 1)',   // teal
  'rgba(99, 102, 241, 1)',   // indigo
  'rgba(217, 70, 239, 1)',   // fuchsia
];

export function CompareView({ mindshareData, loading, subnetHistories = {} }: CompareViewProps) {
  const { isPremium, loading: checkingPremium } = usePremiumStatus();
  const [selectedSubnets, setSelectedSubnets] = useState<SelectedSubnet[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMetrics, setSelectedMetrics] = useState<Record<MetricType, boolean>>({
    mindsharePercentage: true,
    twitterEngagement: false,
    marketCapScore: false,
    rankScore: false,
    volumeScore: false
  });
  const [overlayMode, setOverlayMode] = useState(false);
  const chartRef = useRef<any>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // Filter subnets based on search term - memoized for performance
  const filteredSubnets = useMemo(() => {
    return mindshareData.filter(subnet => 
      subnet.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subnet.netuid.toString().includes(searchTerm)
    );
  }, [mindshareData, searchTerm]);

  // Load history data for a subnet
  const loadSubnetHistory = async (netuid: number) => {
    try {
      setSelectedSubnets(prev => 
        prev.map(s => s.netuid === netuid ? { ...s, loading: true } : s)
      );
      
      // Check if we already have prerendered history data
      if (subnetHistories && subnetHistories[netuid]) {
        setSelectedSubnets(prev => 
          prev.map(s => s.netuid === netuid ? { 
            ...s, 
            history: subnetHistories[netuid], 
            loading: false 
          } : s)
        );
        return;
      }
      
      const history = await getTensorpulseMindshareHistory(netuid);
      
      // Filter for last 24 hours and sample every 15th point (increased from 10 to 15)
      if (history) {
        const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
        const filteredHistory = {
          ...history,
          history: history.history
            .filter(entry => new Date(entry.timestamp) >= oneDayAgo)
            .filter((_, index) => index % 15 === 0) // Sample every 15th point
        };
        
        setSelectedSubnets(prev => 
          prev.map(s => s.netuid === netuid ? { ...s, history: filteredHistory, loading: false } : s)
        );
      }
    } catch (error) {
      console.error(`Failed to load history for subnet ${netuid}:`, error);
      
      setSelectedSubnets(prev => 
        prev.map(s => s.netuid === netuid ? { ...s, loading: false } : s)
      );
    }
  };

  // Add a subnet to comparison
  const addSubnet = (subnet: SubnetMindshare) => {
    if (selectedSubnets.length >= 5) {
      alert('You can compare up to 5 subnets at a time');
      return;
    }
    
    if (selectedSubnets.some(s => s.netuid === subnet.netuid)) {
      return;
    }
    
    const colorIndex = selectedSubnets.length % COLORS.length;
    
    const newSubnet: SelectedSubnet = {
      netuid: subnet.netuid,
      name: subnet.name,
      color: COLORS[colorIndex],
      history: null,
      loading: true
    };
    
    setSelectedSubnets(prev => [...prev, newSubnet]);
    loadSubnetHistory(subnet.netuid);
  };

  // Remove a subnet from comparison
  const removeSubnet = (netuid: number) => {
    setSelectedSubnets(prev => prev.filter(s => s.netuid !== netuid));
  };

  // Toggle a metric
  const toggleMetric = (metric: MetricType) => {
    setSelectedMetrics(prev => ({
      ...prev,
      [metric]: !prev[metric]
    }));
  };

  // Apply a simple moving average to smooth the data
  const applyMovingAverage = (data: number[], windowSize: number = 3): number[] => {
    if (data.length < windowSize) return data;
    
    const result: number[] = [];
    
    for (let i = 0; i < data.length; i++) {
      let sum = 0;
      let count = 0;
      
      for (let j = Math.max(0, i - Math.floor(windowSize / 2)); 
           j <= Math.min(data.length - 1, i + Math.floor(windowSize / 2)); 
           j++) {
        sum += data[j];
        count++;
      }
      
      result.push(sum / count);
    }
    
    return result;
  };

  // Download chart as PNG
  const downloadChart = async () => {
    if (!chartRef.current) return;
    
    setIsDownloading(true);
    
    try {
      const subnetNames = selectedSubnets.map(s => s.name.replace(/\s+/g, '_')).join('-');
      const metricNames = Object.entries(selectedMetrics)
        .filter(([_, isSelected]) => isSelected)
        .map(([metric]) => metric.replace(/([A-Z])/g, '_$1').toLowerCase())
        .join('-');
      
      const filename = `tensorpulse-compare-${subnetNames}-${metricNames}-${new Date().toISOString().split('T')[0]}.png`;
      
      let profileImageUrl: string | undefined;
      let subnetName: string | undefined;
      let netuid: number | undefined;
      
      if (selectedSubnets.length > 0) {
        const firstSubnet = selectedSubnets[0];
        subnetName = firstSubnet.name;
        netuid = firstSubnet.netuid;
        
        const subnetInfo = subnetDescriptions[firstSubnet.netuid];
        if (subnetInfo?.x) {
          const xUrlMatch = subnetInfo.x.match(/twitter\.com\/(\w+)|x\.com\/(\w+)/i);
          if (xUrlMatch) {
            const xUsername = (xUrlMatch[1] || xUrlMatch[2] || '').toLowerCase();
            profileImageUrl = `https://unavatar.io/twitter/${xUsername}`;
          }
        }
      }
      
      const success = await downloadChartAsPng(chartRef, filename, {
        subnetName: subnetName || 'Subnet Comparison',
        profileImageUrl,
        netuid
      });
      
      if (!success) {
        throw new Error('Failed to download chart');
      }
    } catch (error) {
      console.error('Error downloading chart:', error);
      alert('Failed to download chart. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  // Prepare chart data - memoized for performance
  const getChartData = useMemo(() => {
    const activeMetrics = Object.entries(selectedMetrics)
      .filter(([_, isActive]) => isActive)
      .map(([metric]) => metric as MetricType);
    
    if (activeMetrics.length === 0 || selectedSubnets.length === 0) {
      return {
        labels: [],
        datasets: []
      };
    }
    
    const subnetWithMostData = selectedSubnets
      .filter(s => s.history && s.history.history.length > 0)
      .sort((a, b) => 
        (b.history?.history.length || 0) - (a.history?.history.length || 0)
      )[0];
    
    if (!subnetWithMostData || !subnetWithMostData.history) {
      return {
        labels: [],
        datasets: []
      };
    }
    
    const labels = subnetWithMostData.history.history.map(entry => new Date(entry.timestamp));
    
    const datasets = selectedSubnets
      .filter(subnet => subnet.history && subnet.history.history.length > 0)
      .flatMap(subnet => {
        return activeMetrics.map(metric => {
          let metricName: string;
          switch (metric) {
            case 'mindsharePercentage':
              metricName = 'Mindshare';
              break;
            case 'twitterEngagement':
              metricName = 'Twitter';
              break;
            case 'marketCapScore':
              metricName = 'Market Cap';
              break;
            case 'rankScore':
              metricName = 'Rank';
              break;
            case 'volumeScore':
              metricName = 'Volume';
              break;
            default:
              metricName = metric;
          }
          
          const rawData = subnet.history!.history.map(entry => entry[metric]);
          const smoothedData = applyMovingAverage(rawData);
          
          return {
            label: `${subnet.name} - ${metricName}`,
            data: smoothedData,
            borderColor: subnet.color,
            backgroundColor: subnet.color.replace('1)', '0.1)'),
            borderWidth: 2,
            tension: 0.4,
            fill: false,
            pointRadius: 0,
            pointHoverRadius: 5,
            yAxisID: overlayMode ? 'y' : `y-${subnet.netuid}-${metric}`
          };
        });
      });
    
    return {
      labels,
      datasets
    };
  }, [selectedSubnets, selectedMetrics, overlayMode]);

  // Chart options - memoized for performance
  const getChartOptions = useMemo(() => {
    const baseOptions = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index' as const,
        intersect: false,
      },
      plugins: {
        legend: {
          position: 'top' as const,
          labels: {
            color: 'rgba(255, 255, 255, 0.8)',
            font: {
              size: window.innerWidth < 768 ? 10 : 12
            },
            padding: window.innerWidth < 768 ? 10 : 12,
            usePointStyle: true
          }
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: 'rgba(255, 255, 255, 0.8)',
          bodyColor: 'rgba(255, 255, 255, 0.8)',
          padding: window.innerWidth < 768 ? 8 : 12,
          boxPadding: window.innerWidth < 768 ? 4 : 6,
          usePointStyle: true
        }
      }
    };

    const xAxis = {
      type: 'time' as const,
      time: {
        unit: 'hour' as const,
        tooltipFormat: 'PPP',
        displayFormats: {
          hour: 'HH:mm'
        }
      },
      grid: {
        display: false
      },
      ticks: {
        color: 'rgba(255, 255, 255, 0.6)',
        font: {
          size: window.innerWidth < 768 ? 10 : 12
        },
        maxRotation: 0,
        padding: window.innerWidth < 768 ? 5 : 10,
        maxTicksLimit: window.innerWidth < 768 ? 4 : 6 // Reduced number of ticks
      },
      border: {
        display: false
      }
    };

    if (overlayMode) {
      return {
        ...baseOptions,
        scales: {
          x: xAxis,
          y: {
            grid: {
              color: 'rgba(255, 255, 255, 0.1)'
            },
            ticks: {
              color: 'rgba(255, 255, 255, 0.6)',
              font: {
                size: window.innerWidth < 768 ? 10 : 12
              }
            },
            border: {
              display: false
            }
          }
        }
      };
    } else {
      const scales: any = { x: xAxis };
      
      const activeMetrics = Object.entries(selectedMetrics)
        .filter(([_, isActive]) => isActive)
        .map(([metric]) => metric as MetricType);
      
      selectedSubnets.forEach(subnet => {
        activeMetrics.forEach(metric => {
          const axisId = `y-${subnet.netuid}-${metric}`;
          scales[axisId] = {
            type: 'linear' as const,
            display: true,
            position: 'left' as const,
            grid: {
              color: 'rgba(255, 255, 255, 0.1)'
            },
            ticks: {
              color: subnet.color,
              font: {
                size: window.innerWidth < 768 ? 10 : 12
              }
            },
            border: {
              display: false
            }
          };
        });
      });
      
      return {
        ...baseOptions,
        scales
      };
    }
  }, [selectedSubnets, selectedMetrics, overlayMode]);

  if (loading || checkingPremium) {
    return (
      <div className="h-[400px] flex items-center justify-center">
        <div className="animate-pulse text-gray-400">Loading mindshare data...</div>
      </div>
    );
  }

  if (!isPremium) {
    return (
      <div className="relative h-[600px] bg-black/20 backdrop-blur-sm rounded-lg border border-white/5">
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm rounded-lg">
          <div className="flex flex-col items-center text-center p-8">
            <div className="relative mb-6">
              <Swords className="w-16 h-16 text-cyan-400" />
              <Lock className="w-8 h-8 text-cyan-400 absolute -bottom-2 -right-2" />
            </div>
            <h3 className="text-2xl font-bold mb-2">Premium Access Only</h3>
            <p className="text-gray-400 mb-6 max-w-md">
              Stake 10,000 $PULSE tokens to unlock premium features including subnet comparison analytics.
            </p>
            <a
              href="https://creator.bid/agents/676ef7b23bdebce80077f770"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-cyan-600 hover:bg-cyan-700 text-white px-6 py-3 rounded-lg font-medium transition-colors inline-flex items-center gap-2"
            >
              <Lock className="w-5 h-5" />
              Unlock Premium Access
            </a>
          </div>
        </div>
        
        <div className="opacity-10 pointer-events-none">
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-full md:w-1/3 bg-black/20 backdrop-blur-sm rounded-lg p-4 border border-white/5">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <Brain className="w-5 h-5 text-cyan-400 mr-2" />
                  Select Subnets
                </h3>
                <div className="mb-4">
                  <div className="relative">
                    <input
                      type="text"
                      disabled
                      placeholder="Search subnets..."
                      className="w-full bg-black/20 border border-white/10 rounded-lg py-2 px-4 pl-10 text-white placeholder-gray-400"
                    />
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>
              
              <div className="w-full md:w-2/3 bg-black/20 backdrop-blur-sm rounded-lg p-4 border border-white/5">
                <div className="h-[400px] flex items-center justify-center">
                  <div className="text-center text-gray-400">
                    <Brain className="w-12 h-12 mx-auto mb-4 text-gray-500 opacity-50" />
                    <p>Select subnets to compare</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row gap-4">
        <div className="w-full md:w-1/3 bg-black/20 backdrop-blur-sm rounded-lg p-4 border border-white/5">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Brain className="w-5 h-5 text-cyan-400 mr-2" />
            Select Subnets
          </h3>
          
          <div className="mb-4">
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search subnets..."
                className="w-full bg-black/20 border border-white/10 rounded-lg py-2 px-4 pl-10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent text-sm"
              />
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            </div>
          </div>
          
          <div className="mb-4">
            <div className="text-sm text-gray-400 mb-2 flex items-center">
              <Info className="w-4 h-4 mr-1" />
              <span>Selected: {selectedSubnets.length}/5</span>
            </div>
            
            {selectedSubnets.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {selectedSubnets.map(subnet => (
                  <div 
                    key={subnet.netuid}
                    className="flex items-center bg-black/30 rounded-full px-3 py-1 text-xs"
                    style={{ borderLeft: `3px solid ${subnet.color}` }}
                  >
                    <span className="mr-2">{subnet.name}</span>
                    <button
                      onClick={() => removeSubnet(subnet.netuid)}
                      className="text-gray-400 hover:text-white"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="max-h-[300px] overflow-y-auto pr-2 space-y-1">
            {filteredSubnets.map(subnet => {
              const isSelected = selectedSubnets.some(s => s.netuid === subnet.netuid);
              return (
                <button
                  key={subnet.netuid}
                  onClick={() => !isSelected && addSubnet(subnet)}
                  disabled={isSelected}
                  className={`w-full text-left px-3 py-2 rounded-lg flex items-center justify-between ${
                    isSelected 
                      ? 'bg-black/40 text-gray-500 cursor-not-allowed' 
                      : 'bg-black/20 hover:bg-black/30 text-white'
                  }`}
                >
                  <div className="flex items-center">
                    <span className="text-sm">{subnet.name}</span>
                    <span className="text-xs text-gray-500 ml-2">SN{subnet.netuid}</span>
                  </div>
                  {isSelected && <Check className="w-4 h-4 text-green-500" />}
                </button>
              );
            })}
            
            {filteredSubnets.length === 0 && (
              <div className="text-center py-4 text-gray-400 text-sm">
                No subnets found matching "{searchTerm}"
              </div>
            )}
          </div>
        </div>
        
        <div className="w-full md:w-2/3 bg-black/20 backdrop-blur-sm rounded-lg p-4 border border-white/5">
          <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => toggleMetric('mindsharePercentage')}
                className={`px-3 py-1.5 rounded-lg text-xs ${
                  selectedMetrics.mindsharePercentage 
                    ? 'bg-cyan-600 text-white' 
                    : 'bg-black/20 text-gray-300 hover:bg-black/30'
                }`}
              >
                Mindshare
              </button>
              <button
                onClick={() => toggleMetric('twitterEngagement')}
                className={`px-3 py-1.5 rounded-lg text-xs ${
                  selectedMetrics.twitterEngagement 
                    ? 'bg-cyan-600 text-white' 
                    : 'bg-black/20 text-gray-300 hover:bg-black/30'
                }`}
              >
                Twitter
              </button>
              <button
                onClick={() => toggleMetric('marketCapScore')}
                className={`px-3 py-1.5 rounded-lg text-xs ${
                  selectedMetrics.marketCapScore 
                    ? 'bg-cyan-600 text-white' 
                    : 'bg-black/20 text-gray-300 hover:bg-black/30'
                }`}
              >
                Market Cap
              </button>
              <button
                onClick={() => toggleMetric('rankScore')}
                className={`px-3 py-1.5 rounded-lg text-xs ${
                  selectedMetrics.rankScore 
                    ? 'bg-cyan-600 text-white' 
                    : 'bg-black/20 text-gray-300 hover:bg-black/30'
                }`}
              >
                Rank
              </button>
              <button
                onClick={() => toggleMetric('volumeScore')}
                className={`px-3 py-1.5 rounded-lg text-xs ${
                  selectedMetrics.volumeScore 
                    ? 'bg-cyan-600 text-white' 
                    : 'bg-black/20 text-gray-300 hover:bg-black/30'
                }`}
              >
                Volume
              </button>
              
              <button
                onClick={() => setOverlayMode(!overlayMode)}
                className={`px-3 py-1.5 rounded-lg text-xs flex items-center ${
                  overlayMode 
                    ? 'bg-cyan-600 text-white' 
                    : 'bg-black/20 text-gray-300 hover:bg-black/30'
                }`}
              >
                <Layers className="w-3 h-3 mr-1" />
                {overlayMode ? 'Disable Overlay' : 'Enable Overlay'}
              </button>
            </div>
            
            {selectedSubnets.length > 0 && !selectedSubnets.every(s => s.history === null) && (
              <button
                onClick={downloadChart}
                disabled={isDownloading}
                className={`px-3 py-1.5 rounded-lg text-xs flex items-center ${
                  isDownloading 
                    ? 'bg-gray-600 text-gray-300 cursor-not-allowed' 
                    : 'bg-white/10 hover:bg-white/20 text-white'
                }`}
              >
                <Download className={`w-3 h-3 mr-1 ${isDownloading ? 'animate-pulse' : ''}`} />
                {isDownloading ? 'Downloading...' : 'Download PNG'}
              </button>
            )}
          </div>
          
          <div className="h-[400px] relative">
            {selectedSubnets.length === 0 ? (
              <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <Brain className="w-12 h-12 mx-auto mb-4 text-gray-500 opacity-50" />
                  <p>Select subnets to compare</p>
                </div>
              </div>
            ) : selectedSubnets.every(s => s.history === null) ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="animate-pulse text-gray-400">Loading subnet data...</div>
              </div>
            ) : (
              <Line ref={chartRef} data={getChartData} options={getChartOptions} />
            )}
          </div>
          
          {selectedSubnets.length > 0 && (
            <div className="mt-4 text-xs text-gray-400 text-center">
              Comparing {selectedSubnets.length} subnet{selectedSubnets.length !== 1 ? 's' : ''}. 
              {overlayMode 
                ? ' Overlay mode enabled: all metrics share the same scale.' 
                : ' Each metric has its own scale for better visibility.'}
              <div>Showing sampled data (1 in 15 points) from last 24h for better visualization</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}